import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { forgotPassword } from '../services/authApiClient'; // Import directly from API client
import FormInput from '../components/FormInput';
import FormButton from '../components/FormButton';
import ValidationMessage from '../components/ValidationMessage';
import FuturaSmileIcon from '../components/icons/FuturaSmileIcon';
import ChevronLeftIcon from '../components/icons/ChevronLeftIcon';
import { validateEmail } from '../utils/validation';
import toast from 'react-hot-toast';

const ForgotPasswordPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [emailValidation, setEmailValidation] = useState<'idle' | 'valid' | 'invalid'>('idle');
    const [emailError, setEmailError] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validation = validateEmail(email);
        if (!validation.valid) {
            setEmailError(validation.error || '');
            setEmailValidation('invalid');
            return;
        }

        setIsSubmitting(true);
        try {
            await forgotPassword(email);
            setIsSuccess(true);
            toast.success('Password reset email sent!');
        } catch (err: any) {
            console.error('Forgot password error:', err);
            toast.error(err.message || 'Failed to send reset email');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 flex">
            {/* Decorative Branding Panel - Hidden on mobile */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                    style={{
                        backgroundImage: 'url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80)'
                    }}
                />
                <div className="absolute inset-0 bg-black/60" />
                <div className="relative z-10 flex flex-col justify-between p-8 h-full">
                    <div className="flex justify-between items-start">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 rounded-full bg-teal-600 shadow-lg">
                                <FuturaSmileIcon className="w-6 h-6 text-white" />
                            </div>
                            <h1 className="text-2xl font-bold text-white tracking-widest uppercase">
                                FuturaSmile
                            </h1>
                        </div>
                        <motion.button
                            onClick={() => navigate('/')}
                            whileHover={{ x: -2 }}
                            className="flex items-center space-x-2 px-4 py-2 rounded-full border border-gray-300 text-gray-200 hover:bg-white/10 transition-all duration-300"
                        >
                            <ChevronLeftIcon className="w-4 h-4" />
                            <span className="text-sm font-medium">Back to website</span>
                        </motion.button>
                    </div>
                    <div className="max-w-md">
                        <blockquote className="text-2xl font-serif text-white leading-relaxed">
                            "Security is not just a feature, it's our promise."
                        </blockquote>
                    </div>
                </div>
            </div>

            {/* Form Panel */}
            <div className="flex-1 flex items-center justify-center p-4 lg:p-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-md"
                >
                    <div className="mb-8">
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                        >
                            <h1 className="text-3xl font-bold text-gray-200 mb-2">
                                Forgot Password?
                            </h1>
                            <p className="text-gray-400">
                                {isSuccess
                                    ? 'Check your email for the reset link.'
                                    : 'Enter your email address and we will send you a link to reset your password.'}
                            </p>
                        </motion.div>
                    </div>

                    {!isSuccess ? (
                        <motion.form
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="space-y-6"
                            onSubmit={handleSubmit}
                        >
                            <FormInput
                                id="email"
                                name="email"
                                type="email"
                                label="Email address"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e);
                                    if (emailError) setEmailError('');
                                }}
                                placeholder="Enter your email"
                                required
                                autoComplete="email"
                                validationState={emailValidation}
                                error={emailError}
                            />

                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                            >
                                <FormButton
                                    type="submit"
                                    loading={isSubmitting}
                                    disabled={isSubmitting}
                                    fullWidth
                                    size="lg"
                                    variant="teal"
                                >
                                    {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                                </FormButton>
                            </motion.div>
                        </motion.form>
                    ) : (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-green-900/30 border border-green-800 rounded-lg p-6 text-center"
                        >
                            <div className="mb-4 flex justify-center">
                                <div className="bg-green-800/50 p-3 rounded-full">
                                    <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-xl font-semibold text-green-300 mb-2">Check your email</h3>
                            <p className="text-gray-300 mb-6">
                                We have sent a password reset link to <strong>{email}</strong>.
                            </p>
                            <FormButton
                                variant="secondary"
                                onClick={() => setIsSuccess(false)} // Or allow resend?
                                className="w-full"
                            >
                                Resend Email
                            </FormButton>
                        </motion.div>
                    )}

                    <div className="mt-8 text-center">
                        <Link
                            to="/login"
                            className="font-medium text-teal-400 hover:text-teal-300 transition-colors flex items-center justify-center gap-2"
                        >
                            <ChevronLeftIcon className="w-4 h-4" />
                            Back to Sign In
                        </Link>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
